import React, { Suspense, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useAllCashflowSettingsOverrides } from './logic/useAllCashflowSettingsOverrides';
import {
  DropMenu,
  type DropMenuItem,
  GetColor,
  Headline2,
  Headline3,
  ItemIcon,
  ItemType,
  Link as LinkStyle,
  Loading,
  PRIVATE_ASSET_CASH_FLOW_HELP_HREF,
} from 'venn-ui-kit';
import { Modal, ModalFooter, ModalSize } from '../../modal';
import { isEmpty, noop } from 'lodash';
import { useActiveCashflowSetting } from './logic/useActiveCashflowSetting';
import { CashflowActiveSettingInfo } from './CashflowSettingInfo';
import {
  CashflowSettingOption,
  CLASSIFICATION_SETTING_EXPLANATION,
  DEFAULT_SETTING_EXPLANATION,
  PARAMETER_SETTING_EXPLANATION,
} from './CashflowSettingOption';
import { type CashFlowSetting, type PrivateFund, type PrivatePortfolioNode } from 'venn-api';
import { useSystemCashflowSetting } from './logic/useSystemCashflowSetting';
import { originalAnalysisSubjectQuery } from 'venn-state';
import { useRecoilValue } from 'recoil';

type CashflowSettingsModalProps = {
  onClose: () => void;
  /** Portfolio from which user opened the modal. Can be undefined if user entered the modal from an analysis subject being a fund. */
  privatePortfolio?: PrivatePortfolioNode;
  /** Taken from the private portfolio if not provided */
  subjectName?: string;
  /** Fund which triggered the modal.
   * When multiple funds are available within a portfolio, it populates initially selected option.
   * If portfolio is undefined (a.k.a. viewing fund settings only), the dropdown will not appear,
   * and the user is unable to choose a different fund */
  triggeringFundId?: string;
};

type SettingOverrideType = 'SYSTEM' | 'CLASSIFICATION' | 'PARAMETER' | 'UNKNOWN';

const getOverrideTypeFromActiveSetting = (setting: CashFlowSetting | undefined): SettingOverrideType => {
  if (!setting || !setting.userCreated) {
    return 'SYSTEM';
  }
  return setting.overrideType;
};

type FundCashflowSettingsProps = {
  privateFund: PrivateFund;
};

type FundCashflowSettingsWrapperProps = {
  selectedFundId: string;
};
const FundCashflowSettings = ({ privateFund }: FundCashflowSettingsProps) => {
  const { data: allUserSettings } = useAllCashflowSettingsOverrides();
  const { data: activeSettings } = useActiveCashflowSetting([[privateFund.id, privateFund.name]]);
  const { data: systemSetting } = useSystemCashflowSetting(privateFund);
  const activeSetting = activeSettings?.[0]?.setting;
  const [activeType, setActiveType] = useState<SettingOverrideType>(getOverrideTypeFromActiveSetting(activeSetting));
  if (!allUserSettings || !activeSetting || !systemSetting) {
    return null;
  }
  const { classificationSettings, parameterSettings } = allUserSettings;
  return (
    <>
      <ContentContainer>
        <ChooseSettingContainer>
          <CashflowSettingOption
            title="Venn Default Setting"
            id="SYSTEM"
            activeSettingId={activeSetting.settingId}
            explanation={DEFAULT_SETTING_EXPLANATION}
            expanded={activeType === 'SYSTEM'}
            onRadioClick={() => setActiveType('SYSTEM')}
            options={[systemSetting]}
          />
          <CashflowSettingOption
            title="Classification Override"
            id="CLASSIFICATION"
            activeSettingId={activeSetting.settingId}
            explanation={CLASSIFICATION_SETTING_EXPLANATION}
            expanded={activeType === 'CLASSIFICATION'}
            onRadioClick={() => setActiveType('CLASSIFICATION')}
            options={classificationSettings}
          />
          <CashflowSettingOption
            title="Parameter Override"
            id="PARAMETER"
            activeSettingId={activeSetting.settingId}
            explanation={PARAMETER_SETTING_EXPLANATION}
            expanded={activeType === 'PARAMETER'}
            onRadioClick={() => setActiveType('PARAMETER')}
            options={parameterSettings}
          />
        </ChooseSettingContainer>
        <CashflowActiveSettingInfo setting={activeSetting} />
      </ContentContainer>
    </>
  );
};

const FundCashflowSettingsWrapper = ({ selectedFundId }: FundCashflowSettingsWrapperProps) => {
  const privateFund = useRecoilValue(
    originalAnalysisSubjectQuery({
      privateFundId: selectedFundId,
    }),
  )?.privateFund;

  if (!privateFund) {
    return null;
  }

  return <FundCashflowSettings privateFund={privateFund} />;
};

const DROPDOWN_WIDTH = 1024 - 2 * 24;

export const CashflowSettingsModal = ({
  onClose,
  privatePortfolio,
  triggeringFundId,
  subjectName,
}: CashflowSettingsModalProps) => {
  const [selectedFundId, setSelectedFundId] = useState<string | undefined>(triggeringFundId);
  const dropdownItems: DropMenuItem<string>[] = useMemo(
    () =>
      privatePortfolio?.children?.map((node) => {
        return {
          label: node.name,
          value: node.fundId,
        };
      }) ?? [],
    [privatePortfolio?.children],
  );
  return (
    <Modal size={ModalSize.XXLarge} onClose={onClose} noPadding closeOnEsc closeOnOutsideClick={false}>
      <MainContainer dropdownHeight={privatePortfolio ? 82 : 0} data-testid="cashflow-settings-modal-container">
        <CashflowSettingsModalContainer>
          <SubjectHeader>
            <StyledHeadline2>Cash Flow Pacing Model Settings</StyledHeadline2>
            <StyledHeadline3>
              <ItemIcon item={privatePortfolio ? ItemType.PrivatePortfolio : ItemType.PrivateInvestment} />
              {subjectName ?? privatePortfolio?.name}
            </StyledHeadline3>
            <MoreQuestionsContainer>
              Have more questions? Visit the
              <LinkStyle>
                <button
                  type="button"
                  onClick={() => window.open(PRIVATE_ASSET_CASH_FLOW_HELP_HREF, '_blank')}
                  data-testid="qa-cf-pacing-params-faq-button"
                >
                  &nbsp; FAQ
                </button>
              </LinkStyle>
            </MoreQuestionsContainer>
          </SubjectHeader>
          {!!privatePortfolio && (
            <ChooseFundContainer>
              <b>Select a fund to view parameter settings</b>
              <DropMenu
                placeholder={isEmpty(dropdownItems) ? 'Your portfolio is empty' : 'Not selected yet'}
                items={dropdownItems}
                selected={selectedFundId}
                className="qa-select-fund-in-portfolio-dropdown"
                width={DROPDOWN_WIDTH}
                onChange={(fundId: DropMenuItem<string>) => setSelectedFundId(fundId.value)}
                disabled={false}
                minimumItemsToTrigger={0}
              />
            </ChooseFundContainer>
          )}
          <FlexWrapper>
            <Suspense fallback={<Loading title="Cashflow pacing model parameters are loading... " />}>
              {selectedFundId !== undefined && <FundCashflowSettingsWrapper selectedFundId={selectedFundId} />}
            </Suspense>
          </FlexWrapper>

          <ModalFooter primaryLabel="APPLY CHANGES & CLOSE" primaryDisabled onPrimaryClick={noop} onCancel={onClose} />
        </CashflowSettingsModalContainer>
      </MainContainer>
    </Modal>
  );
};

const MainContainer = styled.div<{ dropdownHeight: number }>`
  ${(props) => css`
    height: ${props.dropdownHeight + 660}px;
  `}
  display: flex;
  flex-direction: column;
`;

const CashflowSettingsModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FlexWrapper = styled.div`
  display: flex;
  flex: 1 1 0;
`;

const SubjectHeader = styled.div`
  display: flex;
  padding: 20px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  height: 136px;
`;

const StyledHeadline2 = styled(Headline2)`
  margin: 0;
`;
const StyledHeadline3 = styled(Headline3)`
  display: flex;
  gap: 4px;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 0;
`;

const MoreQuestionsContainer = styled.span`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: normal;
  line-height: 18px;
  color: ${GetColor.DarkGrey};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 24px 24px 24px;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
`;

const ChooseSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 690px;
  gap: 16px;
`;

const ChooseFundContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px 24px;
  align-items: flex-start;
  gap: 4px;
`;
